import { Identifier, Record } from 'react-admin';

export interface GroupMetadataAvatarType extends Record {
    name: string;
}

export interface MetadataAvatarType extends Record {
    name: string;
    alphaId: string;
}

export interface MetadataAvatarTypeProperty extends Record {
    name: string;
    definition: string;
    fieldType: string;
    option: string;
    order: number;
    isEncrypted: boolean;
    values: any[];
    metadataAvatarType: Identifier;
}

export interface AvatarProperty extends Record {
    name: string;
    definition: string;
    fieldType: string;
    option: string;
    order: number;
    isEncrypted: boolean;
    value: any;
    unit: string;
    languages: string[];
}

export interface MC extends Record {
    avatar: string;
    creationDate: string;
    pairedUp: boolean;
    yourls: string;
}

export interface Avatar extends Record {
    name: string;
    alphaId: string;
    metadataAvatarType: MetadataAvatarType | string;
    mcs: string[] | MC[];
    currentReferencing?: {
        standardReferencing?: {
            runner?: {
                node: Node;
                firstNode: Node;
            };
        };
    };
}

export interface Yourls extends Record {
    short: string;
    mc: string;
    mcUrl: string;
    finger: string;
    routeParameters: string[];
    creationDate: string;
    isActive: boolean;
}

export interface MCNote extends Record {
    createdAt: string;
    data: {
        customFields?: {
            tags: string[];
            thresholdColor: string;
            [key: string]: any;
        };
        [key: string]: any;
    };
    itemId: string;
}

export interface SourceBoard extends Record {
    id: string;
    productTypology: string;
    productTypeName: string;
    productTypeArticle: string;
    productSerialNumber: string;
    productPostalCode: string | null;
    productInstallationDate: string | null;
    productCurrentStatusName: string | null;
    productCurrentStatusDate: string | null;
    productLastMaintenanceDate: string | null;
    productLastEmptyingDate: string | null;
    productLastMaintenanceData: string | null;
    productLastEmptyingData: string | null;
    avatarId: string;
    metadataAvatarTypeId: string;
    companyId: string;
    name: string;
    description: string | null;
    latitude: string | null;
    longitude: string | null;
    elevation: string | null;
    street: string | null;
    additionalAddress: string | null;
    postalCode: string | null;
    city: string | null;
    country: string | null;
    addresstext: string | null;
    creationDate: string | null;
    descriptionpos: string | null;
    comment: string | null;
    supplements: string | null;
    alphaId: string;
    enabled: boolean;
    enableddate: string | null;
    identifiers: string | null;
    currentNode: string | null;
    locationId: string | null;
    locationPlaceId: string | null;
    updatedAt: string | null;
    lft: number;
    rgt: number;
    lvl: number;
    rootId: string;
    parentId: string | null;
    deleted: boolean;
}

/**
 * One row of the rikutec_board_v2_view materialized view, as served by
 * GET /rikutec/source_boards_v2 (statisticsservice). Mirrors the Doctrine entity
 * App\Entity\Dwh\Rikutec\RikutecBoardV2View one-for-one: 112 columns, all nullable but the id.
 *
 * Property names match SourceBoard (v1) wherever the column exists in both views.
 */
export interface SourceBoardV2 extends Record {
    productTypology: string | null;
    productTypeName: string | null;
    productTypeAlphaId: string | null;
    groupMetadataAvatarTypeId: string | null;
    productSerialNumber: string | null;
    productPostalCode: string | null;
    productInstallationDate: string | null;
    productCurrentStatusName: string | null;
    productCurrentStatusDate: string | null;
    productLastMaintenanceDate: string | null;
    productLastEmptyingDate: string | null;
    productLastMaintenanceData: string | null;
    productLastMaintenanceThresholdName: string | null;
    productLastEmptyingData: string | null;
    productLastEmptyingThresholdName: string | null;
    processingCapacity: string | null;
    sludgeStorageVolume: string | null;
    totalUsefulVolume: string | null;
    model: string | null;
    productCode: string | null;
    readyToDeliverDate: string | null;
    poseDate: string | null;
    reserveLiftDate: string | null;
    form2DateInstallationOrInvoice: string | null;
    form2Model: string | null;
    form2NominalCapacity: string | null;
    form2SerialNumber: string | null;
    clientLastName: string | null;
    clientName: string | null;
    clientAddress: string | null;
    clientPostalCode: string | null;
    clientCity: string | null;
    clientCountry: string | null;
    clientEmail: string | null;
    clientPhoneNumber: string | null;
    installationAddress: string | null;
    installationPostalCode: string | null;
    installationCity: string | null;
    installationCountry: string | null;
    clientFullName: string | null;
    installationFullAddress: string | null;
    installerCompany: string | null;
    installerLastName: string | null;
    installerAddress: string | null;
    installerPostalCode: string | null;
    installerCity: string | null;
    installerCountry: string | null;
    installerEmail: string | null;
    installerPhoneNumber: string | null;
    distributorCompany: string | null;
    distributorLastName: string | null;
    distributorAddress: string | null;
    distributorPostalCode: string | null;
    distributorCity: string | null;
    distributorCountry: string | null;
    distributorEmail: string | null;
    distributorPhoneNumber: string | null;
    soilTypology: string | null;
    form8StableSoilBeddingGravel: string | null;
    form8StableSoilSideBackfilling: string | null;
    form9ImpermeableReinforcedSlab: string | null;
    form9ImpermeableLeanConcreteBedding: string | null;
    form9ImpermeableLateralBackfilling: string | null;
    form10UnstableLeanConcreteBedding: string | null;
    form10UnstableLateralBackfilling: string | null;
    form7WaterRunoff: string | null;
    form7SteepSlope: string | null;
    form7DevicesInWater: string | null;
    form7EvacuationAndVentilation: string | null;
    form7NoLoadWithin3Meters: string | null;
    form7RapidRecovery: string | null;
    form7NotInFloodProneArea: string | null;
    form11WaterDecanter: string | null;
    form11BasketInPlace: string | null;
    form11DownstreamLiftingDevice: string | null;
    form12BiologicalWaterReactor: string | null;
    form12GravityFlow: string | null;
    form12BiologicalFilterVentilation: string | null;
    form13ClosedAndSecureBuffers: string | null;
    form14OwnerInformed: string | null;
    receptionQuoteNumber: string | null;
    receptionWorkInformation: string | null;
    receptionWithReserve: string | null;
    conditionsOfUseAndMaintenanceReceived: string | null;
    receptionMadeIn: string | null;
    receptionDate: string | null;
    privacyPolicyAccepted: string | null;
    reserveKindOfReserve: string | null;
    reserveWorkToBePerformed: string | null;
    reserveWorkPeriod: string | null;
    reserveMadeIn: string | null;
    reserveDate: string | null;
    hasOwnerSignature: boolean | null;
    hasInstallerSignature: boolean | null;
    id: string;
    avatarId: string | null;
    metadataAvatarTypeId: string | null;
    companyId: string | null;
    name: string | null;
    description: string | null;
    latitude: string | null;
    longitude: string | null;
    elevation: string | null;
    street: string | null;
    additionalAddress: string | null;
    postalCode: string | null;
    city: string | null;
    country: string | null;
    addresstext: string | null;
    creationDate: string | null;
    descriptionpos: string | null;
    comment: string | null;
    supplements: string | null;
    alphaId: string | null;
    enabled: boolean | null;
    enableddate: string | null;
    identifiers: string | null;
    currentNode: string | null;
    locationId: string | null;
    locationPlaceId: string | null;
    updatedAt: string | null;
    lft: number | null;
    rgt: number | null;
    lvl: number | null;
    rootId: string | null;
    parentId: string | null;
    deleted: boolean | null;
}

export interface TraceabilityNotesThresholdExecutionsAppliedOnAtPropertyCreatedAtMetadata {
    id: number;
    max: number;
    min: number;
    color: string;
    name: string;
    durationType: string;
}

export interface TraceabilityNotesThresholdExecutions {
    id?: Identifier;
    businessProcessIds: string[];
    businessLogicMetadata: string[];
    metadataAvatarType: string | null;
    groupAvatar?: string | null;
    avatar?: string | null;
    appliedOn: string;
    appliedOnToLastItem: boolean;
    appliedOnAtProperty: string;
    appliedOnNoteAtTag: string;
    appliedOnAtPropertyCreatedAtComparingType: string;
    appliedOnAtPropertyCreatedAtMetadata: TraceabilityNotesThresholdExecutionsAppliedOnAtPropertyCreatedAtMetadata[];
}

export interface CompanyLogo {
    id: string;
    name: string;
}

export interface UserImage {
    id: string;
    name: string;
}

export interface Company {
    '@id': string;
    id: string;
    name: string;
    logo: CompanyLogo;
    /** Company category ints (e.g. 65534 = FABRICANT_ANC). May be absent on older payloads. */
    categories?: number[];
}

export interface User extends Record {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    username: string;
    company: Company;
    image?: UserImage;
    originId: string;
}

export interface Media extends Record {
    id: string;
    name: string;
    contentUrlTokenAccess: string;
    source: string;
}

export interface Node {
    id: string;
    name: string;
    extraDataClassmapper: string;
    extraData: any;
    dateOfTakesPlace: string;
    iconId: string;
    nextNodes: any[];
    configuration: {
        [key: string]: any;
        idForNodeHumanReadable: string;
        idWhenNodeWasAsAnArrayInRunnerTemplate: string;
    };
    nodeTypes: any;
}

export interface RunnerTemplate {
    id: string;
    name: string;
    description: string;
    isDeployed: boolean;
    creationDate: string;
    node: Node;
}

export interface TraceabilityHistory extends Record {
    createdAt: string;
    companyId: string;
    data: any[];
    itemId: string;
    avatarId: string;
    classMapper: string;
    role: string;
    id: 0;
    typeNoteId: string;
    itemResource: string;
    noteId: string;
    name?: string;
    configuration: {
        [key: string]: any;
        idForNodeHumanReadable: string;
    };
}

/** History node names used as the product installation date in the backoffice. */
export const INSTALLATION_DATE_NODE_NAMES = [
    'Travaux de pose effectués',
    'Travaux de pose effectués avec reserves',
] as const;

export interface FileSystemMapper extends Record {}
